import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
const ExploreHub = ({ API, showNotification, isDarkMode, appSettings, userToken, setCurrentView }) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('hub');
  // 'learn' (new — free-browse) or 'guess' (existing quiz hub button)
  const [hubTab, setHubTab] = useState('learn');
  const [clips, setClips] = useState([]);
  const [currentClip, setCurrentClip] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false);

  // Listen & Learn state (new tab — free-browse)
  const [learnClips, setLearnClips] = useState([]);
  const [learnLoading, setLearnLoading] = useState(false);
  const [learnSearch, setLearnSearch] = useState('');
  const [learnCategory, setLearnCategory] = useState('Greetings');
  const [playingId, setPlayingId] = useState(null);
  const [learnLabels, setLearnLabels] = useState({});
  const [guessLabels, setGuessLabels] = useState({});
  const [learnAdminCategories, setLearnAdminCategories] = useState([]);
  const currentAudioRef = useRef(null);

  // Mobile drawer for category picker
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  // Viewport detection — mirrors VerbExplorer pattern (isMobile state, not Tailwind classes)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  // Scroll-to-top FAB visibility (mobile only — shown after scrolling past 400px)
  const [showTopBtn, setShowTopBtn] = useState(false);

  const headers = { 'Authorization': `Bearer ${userToken}`, 'Content-Type': 'application/json' };

  // Resize listener — keep isMobile in sync
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll listener — toggle "back to top" FAB
  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Body scroll lock while mobile drawer is open (matches VerbExplorer line 194-199)
  useEffect(() => {
    if (mobileCatOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileCatOpen]);

  // Fetch the free-browse audio list when the learn tab is first opened
  const fetchLearnClips = async () => {
    setLearnLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || API;
      const res = await fetch(`${backendUrl}/api/explore/listen-learn`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLearnClips(data.clips || []);
        setLearnLabels(data.labels || {});
        if (data.guess_labels) setGuessLabels(data.guess_labels);
        setLearnAdminCategories(Array.isArray(data.categories) ? data.categories : []);
      }
    } catch (e) { showNotification('Failed to load audio', 'error'); }
    setLearnLoading(false);
  };

  // Load learn clips on first mount of the hub view (Listen & Learn is default tab)
  useEffect(() => {
    if (activeView === 'hub' && hubTab === 'learn' && learnClips.length === 0 && !learnLoading) {
      fetchLearnClips();
    }
  }, [activeView, hubTab]); // eslint-disable-line

  const playLearnAudio = (clip) => {
    if (!clip.audio_url) { showNotification('Audio not available', 'warning'); return; }
    // Stop currently playing audio if any
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause(); } catch {}
    }
    try {
      // Sound Library URLs require auth — append JWT as query param (HTML5 <audio> can't send headers).
      // Other URLs (e.g. legacy Dropbox) pass through unchanged.
      let src = clip.audio_url;
      if (userToken && src.includes('/api/sound-library/play/') && !src.includes('token=')) {
        src += (src.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(userToken);
      }
      const audio = new Audio(src);
      currentAudioRef.current = audio;
      setPlayingId(clip.id);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => { setPlayingId(null); showNotification('Could not load audio', 'error'); };
      audio.play().catch(() => { setPlayingId(null); showNotification('Audio playback error', 'warning'); });
    } catch (e) { setPlayingId(null); showNotification('Audio error', 'error'); }
  };

  // Derived list for Listen & Learn (apply search + category filter)
  // Prefer admin-managed categories; fall back to deriving from clip data so older deployments still work.
  const adminCatNames = learnAdminCategories
    .map(c => (typeof c === 'string' ? c : (c && (c.name || c.id))))
    .filter(Boolean);
  const derivedCats = Array.from(new Set(learnClips.map(c => c.category || 'general')));
  const learnCategories = ['all', ...(adminCatNames.length > 0 ? adminCatNames : derivedCats)];

  // Per-category palette — mirrors VerbExplorer CAT_STYLES exactly (8 colors per category + dark variant).
  // Each entry: { bg, border, shadow, text, badge, activeBg, activeBorder, activeText, icon }
  const CAT_STYLES = {
    'all':                          { bg: '#eef2ff', border: '#c7d2fe', shadow: 'rgba(99,102,241,0.15)',  text: '#3730a3', badge: '#6366f1', activeBg: '#e0e7ff', activeBorder: '#6366f1', activeText: '#312e81', icon: '📚' },
    'greetings':                    { bg: '#fef3c7', border: '#fcd34d', shadow: 'rgba(245,158,11,0.15)',  text: '#92400e', badge: '#f59e0b', activeBg: '#fde68a', activeBorder: '#f59e0b', activeText: '#78350f', icon: '👋' },
    'politeness':                   { bg: '#fdf2f8', border: '#f9a8d4', shadow: 'rgba(236,72,153,0.15)',  text: '#9d174d', badge: '#ec4899', activeBg: '#fce7f3', activeBorder: '#ec4899', activeText: '#831843', icon: '🙏' },
    'quick replies':                { bg: '#ecfeff', border: '#67e8f9', shadow: 'rgba(6,182,212,0.15)',   text: '#155e75', badge: '#06b6d4', activeBg: '#cffafe', activeBorder: '#06b6d4', activeText: '#164e63', icon: '💬' },
    'about you / understanding':    { bg: '#eff6ff', border: '#bfdbfe', shadow: 'rgba(59,130,246,0.15)',  text: '#1e40af', badge: '#3b82f6', activeBg: '#dbeafe', activeBorder: '#3b82f6', activeText: '#1e3a8a', icon: '🤔' },
    'where / direction':            { bg: '#f0fdf4', border: '#86efac', shadow: 'rgba(34,197,94,0.15)',   text: '#166534', badge: '#22c55e', activeBg: '#dcfce7', activeBorder: '#22c55e', activeText: '#14532d', icon: '🗺️' },
    'when / time':                  { bg: '#faf5ff', border: '#d8b4fe', shadow: 'rgba(168,85,247,0.15)',  text: '#6b21a8', badge: '#a855f7', activeBg: '#f3e8ff', activeBorder: '#a855f7', activeText: '#581c87', icon: '⏰' },
    'what / opinion':               { bg: '#f0fdfa', border: '#5eead4', shadow: 'rgba(20,184,166,0.15)',  text: '#115e59', badge: '#14b8a6', activeBg: '#ccfbf1', activeBorder: '#14b8a6', activeText: '#134e4a', icon: '💭' },
    'asking for help':              { bg: '#fef2f2', border: '#fca5a5', shadow: 'rgba(239,68,68,0.15)',   text: '#991b1b', badge: '#ef4444', activeBg: '#fee2e2', activeBorder: '#ef4444', activeText: '#7f1d1d', icon: '🆘' },
    'commands':                     { bg: '#f8fafc', border: '#94a3b8', shadow: 'rgba(100,116,139,0.15)', text: '#334155', badge: '#64748b', activeBg: '#e2e8f0', activeBorder: '#475569', activeText: '#1e293b', icon: '⚡' },
    'about myself':                 { bg: '#eef2ff', border: '#c7d2fe', shadow: 'rgba(99,102,241,0.15)',  text: '#3730a3', badge: '#6366f1', activeBg: '#e0e7ff', activeBorder: '#6366f1', activeText: '#312e81', icon: '👤' },
    'about others / descriptions':  { bg: '#fefce8', border: '#fde047', shadow: 'rgba(234,179,8,0.15)',   text: '#854d0e', badge: '#eab308', activeBg: '#fef9c3', activeBorder: '#eab308', activeText: '#713f12', icon: '👥' },
  };
  const CAT_STYLES_DARK = {
    'all':                          { bg: '#1e1b4b', border: '#4338ca', shadow: 'rgba(99,102,241,0.25)',  text: '#a5b4fc', badge: '#6366f1', activeBg: '#312e81', activeBorder: '#6366f1', activeText: '#c7d2fe', icon: '📚' },
    'greetings':                    { bg: '#451a03', border: '#b45309', shadow: 'rgba(245,158,11,0.25)',  text: '#fcd34d', badge: '#f59e0b', activeBg: '#78350f', activeBorder: '#f59e0b', activeText: '#fef3c7', icon: '👋' },
    'politeness':                   { bg: '#500724', border: '#be185d', shadow: 'rgba(236,72,153,0.25)',  text: '#f9a8d4', badge: '#ec4899', activeBg: '#831843', activeBorder: '#ec4899', activeText: '#fce7f3', icon: '🙏' },
    'quick replies':                { bg: '#083344', border: '#0e7490', shadow: 'rgba(6,182,212,0.25)',   text: '#67e8f9', badge: '#06b6d4', activeBg: '#164e63', activeBorder: '#06b6d4', activeText: '#cffafe', icon: '💬' },
    'about you / understanding':    { bg: '#172554', border: '#1d4ed8', shadow: 'rgba(59,130,246,0.25)',  text: '#93c5fd', badge: '#3b82f6', activeBg: '#1e3a8a', activeBorder: '#3b82f6', activeText: '#bfdbfe', icon: '🤔' },
    'where / direction':            { bg: '#052e16', border: '#15803d', shadow: 'rgba(34,197,94,0.25)',   text: '#86efac', badge: '#22c55e', activeBg: '#14532d', activeBorder: '#22c55e', activeText: '#dcfce7', icon: '🗺️' },
    'when / time':                  { bg: '#3b0764', border: '#7e22ce', shadow: 'rgba(168,85,247,0.25)',  text: '#d8b4fe', badge: '#a855f7', activeBg: '#581c87', activeBorder: '#a855f7', activeText: '#f3e8ff', icon: '⏰' },
    'what / opinion':               { bg: '#042f2e', border: '#0f766e', shadow: 'rgba(20,184,166,0.25)',  text: '#5eead4', badge: '#14b8a6', activeBg: '#134e4a', activeBorder: '#14b8a6', activeText: '#ccfbf1', icon: '💭' },
    'asking for help':              { bg: '#450a0a', border: '#b91c1c', shadow: 'rgba(239,68,68,0.25)',   text: '#fca5a5', badge: '#ef4444', activeBg: '#7f1d1d', activeBorder: '#ef4444', activeText: '#fee2e2', icon: '🆘' },
    'commands':                     { bg: '#1e293b', border: '#475569', shadow: 'rgba(100,116,139,0.25)', text: '#94a3b8', badge: '#64748b', activeBg: '#334155', activeBorder: '#475569', activeText: '#e2e8f0', icon: '⚡' },
    'about myself':                 { bg: '#1e1b4b', border: '#4338ca', shadow: 'rgba(99,102,241,0.25)',  text: '#a5b4fc', badge: '#6366f1', activeBg: '#312e81', activeBorder: '#6366f1', activeText: '#c7d2fe', icon: '👤' },
    'about others / descriptions':  { bg: '#422006', border: '#a16207', shadow: 'rgba(234,179,8,0.25)',   text: '#fde047', badge: '#eab308', activeBg: '#713f12', activeBorder: '#eab308', activeText: '#fef9c3', icon: '👥' },
  };
  const getCatStyle = (name, dark) => {
    const key = (name || '').toLowerCase();
    return (dark ? CAT_STYLES_DARK : CAT_STYLES)[key]
      || (dark ? CAT_STYLES_DARK : CAT_STYLES)['commands']; // fallback to slate
  };
  // Admin-managed icons override per category — built from /explore/listen-learn response's `categories` field
  const catIconMap = {};
  learnAdminCategories.forEach((c) => {
    if (c && typeof c === 'object' && c.name) {
      catIconMap[c.name.toLowerCase()] = { icon_url: c.icon_url || '', icon: c.icon || '' };
    }
  });
  const renderCatIcon = (name, sizePx = 22) => {
    const cs = getCatStyle(name, isDarkMode);
    const meta = catIconMap[(name || '').toLowerCase()];
    if (meta && meta.icon_url) {
      const base = (process.env.REACT_APP_BACKEND_URL || API).replace('/api', '');
      const src = meta.icon_url.startsWith('http') ? meta.icon_url : `${base}${meta.icon_url}`;
      return <img src={src} alt="" className="object-contain flex-shrink-0" style={{ width: sizePx, height: sizePx }} />;
    }
    const emoji = (meta && meta.icon) ? meta.icon : cs.icon;
    return <span className="flex-shrink-0" style={{ fontSize: sizePx + 'px', lineHeight: 1 }} aria-hidden="true">{emoji}</span>;
  };
  const filteredLearnClips = learnClips.filter(c => {
    if (learnCategory !== 'all' && (c.category || 'general') !== learnCategory) return false;
    if (learnSearch.trim()) {
      const q = learnSearch.toLowerCase();
      return (c.darija_text || '').toLowerCase().includes(q)
          || (c.darija_latin || '').toLowerCase().includes(q)
          || (c.english || '').toLowerCase().includes(q);
    }
    return true;
  });

  const fetchClips = async () => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || API;
      const res = await fetch(`${backendUrl}/api/explore/listen-guess`, { headers });
      if (res.ok) {
        const data = await res.json();
        setClips(data.clips || []);
        setGuessLabels(data.labels || {});
        setCurrentClip(0);
        setAudioPlayed(false);
        setAnswered(false);
        setSelectedOption(null);
        setIsCorrect(null);
      }
    } catch (e) { showNotification('Failed to load clips', 'error'); }
    setIsLoading(false);
  };

  const playAudio = (url) => {
    if (!url) { showNotification('Audio not available', 'warning'); return; }
    try {
      // Sound Library URLs require auth — append JWT as query param (HTML5 <audio> can't send headers).
      // Other URLs (e.g. legacy Dropbox) pass through unchanged. Same shim as playLearnAudio.
      let src = url;
      if (userToken && src.includes('/api/sound-library/play/') && !src.includes('token=')) {
        src += (src.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(userToken);
      }
      const audio = new Audio(src);
      audio.play().then(() => setAudioPlayed(true)).catch(() => showNotification('Audio playback error', 'warning'));
      audio.onerror = () => showNotification('Could not load audio', 'error');
    } catch (e) { showNotification('Audio error', 'error'); }
  };

  // Skip to next clip without recording an answer (for clips the user doesn't know)
  const handleSkip = () => {
    if (answered) return;
    if (currentClip + 1 >= clips.length) {
      fetchClips(); // wrap around to a fresh random batch
    } else {
      setCurrentClip((c) => c + 1);
      setAnswered(false);
      setSelectedOption(null);
      setIsCorrect(null);
      setAudioPlayed(false);
    }
  };

  const handleAnswer = async (optionIdx) => {
    if (answered) return;
    setSelectedOption(optionIdx);
    const clip = clips[currentClip];
    const correct = optionIdx === clip.correct_index;
    setIsCorrect(correct);
    setAnswered(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || API;
      await fetch(`${backendUrl}/api/explore/listen-guess/answer`, {
        method: 'POST', headers,
        body: JSON.stringify({ clip_id: clip.id, is_correct: correct })
      });
    } catch (e) { /* non-critical */ }

    setTimeout(() => {
      if (currentClip + 1 >= clips.length) {
        // End of session: auto-load a fresh random batch — quiz continues indefinitely (no score, no summary)
        fetchClips();
      } else {
        setCurrentClip(c => c + 1);
        setAnswered(false);
        setSelectedOption(null);
        setIsCorrect(null);
        setAudioPlayed(false);
      }
    }, 1500);
  };

  if (activeView === 'hub') {
    // Per-category clip counts (used for desktop cards + mobile drawer badges)
    const catCounts = learnClips.reduce((acc, c) => {
      const k = c.category || 'general';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const totalLearnCount = learnClips.length;
    const currentCatLabel = learnCategory === 'all' ? 'All' : learnCategory;
    const currentCatCount = learnCategory === 'all' ? totalLearnCount : (catCounts[learnCategory] || 0);

    return (
      <div className={`min-h-screen pb-24 ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
        {/* Inline keyframes — matches VerbExplorer exactly (unprefixed names) */}
        <style>{`
@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        <div className="max-w-[1400px] mx-auto px-3 sm:px-5 pt-6">
          {/* Back to Translation */}
          <button onClick={() => setCurrentView('translator')}
            className={`flex items-center gap-1 mb-3 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            back
          </button>

          {/* Hero header (centered, no inner wrapper — matches VerbExplorer flow) */}
          <header className="text-center pt-4 sm:pt-6 pb-3" style={{ background: isDarkMode ? 'transparent' : 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 100%)' }}>
            <div className="flex items-center justify-center gap-3 mb-1">
              <span className="text-3xl sm:text-4xl">🎧</span>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{
                background: isDarkMode ? 'linear-gradient(135deg, #818cf8, #a78bfa, #f472b6)' : 'linear-gradient(135deg, #4f46e5, #7c3aed, #db2777)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>
                {hubTab === 'learn' ? t('explore_listen_learn') : t('explore_darija_header')}
              </h1>
            </div>
            {hubTab === 'learn' && learnLabels.subheading && (
              <p className={`text-sm sm:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {learnLabels.subheading}
              </p>
            )}
          </header>

          {/* Tabs — premium segmented control with gradient active state */}
          <div className="flex justify-center mb-5">
            <div
              className={`inline-flex gap-1.5 p-1.5 rounded-2xl border ${
                isDarkMode
                  ? 'bg-gray-800/80 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}
              style={{
                boxShadow: isDarkMode
                  ? '0 4px 16px rgba(0,0,0,0.4)'
                  : '0 4px 20px rgba(99,102,241,0.08)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {[
                { key: 'learn', icon: '🎧', label: t('explore_listen_learn') },
                { key: 'guess', icon: '🎯', label: t('explore_listen_guess') },
              ].map((tab) => {
                const isActive = hubTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setHubTab(tab.key)}
                    className={`relative flex items-center gap-2 sm:gap-2.5 ${isMobile ? 'px-4 py-2.5 text-sm' : 'px-6 py-3 text-base'} rounded-xl font-bold transition-all duration-300`}
                    style={{
                      background: isActive
                        ? 'linear-gradient(135deg, #7c3aed 0%, #6366f1 50%, #4f46e5 100%)'
                        : 'transparent',
                      color: isActive ? '#ffffff' : (isDarkMode ? '#cbd5e1' : '#475569'),
                      transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                      boxShadow: isActive ? '0 6px 20px rgba(99,102,241,0.4)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = isDarkMode ? 'rgba(75,85,99,0.5)' : 'rgba(243,244,246,0.8)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span className={`${isMobile ? 'text-lg' : 'text-xl'}`}>{tab.icon}</span>
                    <span className="whitespace-nowrap tracking-tight">{tab.label}</span>
                    {/* Active underline glow at bottom */}
                    {isActive && (
                      <span
                        className="absolute -bottom-px left-1/2 -translate-x-1/2 h-0.5 rounded-full"
                        style={{
                          width: '40%',
                          background: 'rgba(255,255,255,0.6)',
                          boxShadow: '0 0 8px rgba(255,255,255,0.8)',
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {hubTab === 'guess' && (
            <div className="flex justify-center">
              <button
                onClick={() => { setActiveView('listen-guess'); fetchClips(); }}
                className={`w-full max-w-2xl p-6 rounded-2xl text-left transition-all transform hover:scale-[1.02] shadow-lg ${
                  isDarkMode ? 'bg-gradient-to-br from-purple-900 to-indigo-900 hover:from-purple-800 hover:to-indigo-800' : 'bg-gradient-to-br from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500'
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-5xl">🎯</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">{guessLabels.button_text || t('explore_start_listen_guess')}</h2>
                    <p className="text-sm text-white/80 mt-1">{guessLabels.description || t('explore_guess_description')}</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {hubTab === 'learn' && (
            <div>
              {/* MOBILE: drawer trigger button (renders ABOVE the flex split) */}
              {isMobile && learnCategories.length > 1 && (() => {
                const cs = getCatStyle(learnCategory === 'all' ? 'all' : learnCategory, isDarkMode);
                const triggerLabel = learnCategory === 'all' ? 'All Categories' : learnCategory;
                return (
                  <button
                    onClick={() => setMobileCatOpen(true)}
                    className={`w-full flex items-center justify-between px-4 py-3 mb-3 rounded-2xl border-2 transition-all ${
                      isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                    style={{ boxShadow: '0 2px 12px rgba(99,102,241,0.1)' }}
                  >
                    <div className="flex items-center gap-3">
                      {renderCatIcon(learnCategory === 'all' ? 'all' : learnCategory, 22)}
                      <span className="font-bold text-sm" style={{ color: isDarkMode ? '#a5b4fc' : '#4f46e5' }}>{triggerLabel}</span>
                    </div>
                    <span className="text-xs text-gray-400">▶</span>
                  </button>
                );
              })()}

              {/* Desktop split: sidebar (left) + main content (right). Mobile: just main content stacked. */}
              <div className={!isMobile ? 'flex gap-5 items-start' : ''}>

                {/* DESKTOP sidebar — sticky vertical category list (Spotify-style) */}
                {!isMobile && learnCategories.length > 1 && (
                  <aside className="flex-shrink-0" style={{ width: '260px' }}>
                    <div
                      className={`sticky rounded-2xl p-2 border ${isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}
                      style={{ top: '1rem', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}
                    >
                      <h3 className={`text-[10px] font-extrabold uppercase tracking-[0.15em] px-3 pt-2 pb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Categories
                      </h3>
                      <div className="space-y-0.5 max-h-[calc(100vh-180px)] overflow-y-auto">
                        {learnCategories.map(cat => {
                          const cs = getCatStyle(cat, isDarkMode);
                          const isActive = learnCategory === cat;
                          const label = cat === 'all' ? 'All' : cat;
                          const count = cat === 'all' ? totalLearnCount : (catCounts[cat] || 0);
                          return (
                            <button
                              key={cat}
                              onClick={() => setLearnCategory(cat)}
                              className="w-full flex items-center gap-2.5 pl-2 pr-2 py-2.5 rounded-xl text-left transition-all"
                              style={{
                                background: isActive ? cs.activeBg : 'transparent',
                              }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = isDarkMode ? 'rgba(75,85,99,0.3)' : 'rgba(243,244,246,0.7)'; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                              {/* Colored left rail when active */}
                              <span
                                style={{
                                  width: '3px',
                                  height: '24px',
                                  borderRadius: '2px',
                                  background: isActive ? cs.activeBorder : 'transparent',
                                  flexShrink: 0,
                                }}
                              />
                              {renderCatIcon(cat, 22)}
                              <span
                                className="flex-1 min-w-0 truncate text-sm"
                                style={{
                                  fontWeight: isActive ? 700 : 600,
                                  color: isActive
                                    ? cs.activeText
                                    : (isDarkMode ? '#cbd5e1' : '#475569'),
                                }}
                              >
                                {label}
                              </span>
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                                style={{
                                  background: isActive ? cs.activeBorder : (isDarkMode ? '#374151' : '#f1f5f9'),
                                  color: isActive ? '#ffffff' : (isDarkMode ? '#9ca3af' : '#64748b'),
                                }}
                              >
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </aside>
                )}

                {/* MAIN content column — search + states + cards */}
                <div className={!isMobile ? 'flex-1 min-w-0' : ''}>

                  {/* Search bar — pill, full-width within the column */}
                  <div className={`mb-5 ${isMobile ? 'flex justify-center px-2' : ''}`}>
                    <div className={`relative ${isMobile ? 'w-full max-w-xs sm:max-w-sm' : 'w-full'}`}>
                      <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        type="text"
                        value={learnSearch}
                        onChange={(e) => setLearnSearch(e.target.value)}
                        placeholder={learnLabels.search_placeholder || 'Search any language...'}
                        className={`w-full pl-10 pr-4 py-2.5 rounded-full text-sm border-2 outline-none transition-all ${
                          isDarkMode
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-purple-500'
                            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500'
                        }`}
                        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                      />
                    </div>
                  </div>

              {learnLoading && (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading audio...</p>
                </div>
              )}

              {!learnLoading && filteredLearnClips.length === 0 && (
                <div className="text-center py-12">
                  <span className="text-5xl block mb-3">🔇</span>
                  <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {learnClips.length === 0 ? (learnLabels.empty_text || 'No audio yet') : 'No matches'}
                  </p>
                  {learnClips.length > 0 && (
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Try a different search
                    </p>
                  )}
                </div>
              )}

              {!learnLoading && filteredLearnClips.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
                  {filteredLearnClips.map((clip) => {
                    const isPlaying = playingId === clip.id;
                    const cs = getCatStyle(clip.category || 'general', isDarkMode);
                    const hasCategory = clip.category && clip.category !== 'general';
                    return (
                      <div
                        key={clip.id}
                        className={`group relative rounded-3xl overflow-hidden transition-all duration-300 ${
                          isPlaying
                            ? 'shadow-2xl scale-[1.02]'
                            : isDarkMode
                              ? 'shadow-md hover:shadow-xl hover:-translate-y-1'
                              : 'shadow-md hover:shadow-xl hover:-translate-y-1'
                        }`}
                        style={{
                          background: isDarkMode
                            ? `linear-gradient(135deg, ${cs.bg} 0%, #1f2937 70%)`
                            : `linear-gradient(135deg, ${cs.bg} 0%, #ffffff 65%)`,
                          border: `1.5px solid ${isPlaying ? cs.activeBorder : cs.border}`,
                        }}
                      >
                        {/* Top row: category pill (left) + play button (right) */}
                        <div className="flex items-start justify-between p-4 pb-2">
                          {hasCategory ? (
                            <span
                              className="text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider font-bold"
                              style={{
                                background: cs.activeBg,
                                color: cs.activeText,
                              }}
                            >
                              {clip.category}
                            </span>
                          ) : <span />}
                          <button
                            onClick={() => playLearnAudio(clip)}
                            aria-label="Play audio"
                            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all transform active:scale-90 shadow-lg ${
                              isPlaying
                                ? 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white animate-pulse'
                                : 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 hover:scale-110'
                            }`}
                          >
                            {isPlaying ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                            ) : (
                              <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            )}
                          </button>
                        </div>

                        {/* Hero content: Arabic centered + Latin under */}
                        <div className="px-5 py-2 text-center min-h-[100px] flex flex-col justify-center">
                          <p
                            className={`text-[28px] font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                            dir="rtl"
                          >
                            {clip.darija_text || '—'}
                          </p>
                          <p
                            className={`text-xs mt-2 font-mono uppercase tracking-[0.15em] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            {clip.darija_latin || '—'}
                          </p>
                        </div>

                        {/* Modern divider with English label in the middle */}
                        <div className="px-5 flex items-center gap-2 my-1">
                          <div
                            className="flex-1 h-px"
                            style={{
                              background: `linear-gradient(to right, transparent, ${isDarkMode ? '#4b5563' : '#d1d5db'}, transparent)`,
                            }}
                          />
                          <span
                            className={`text-[9px] font-bold tracking-widest uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                          >Meaning</span>
                          <div
                            className="flex-1 h-px"
                            style={{
                              background: `linear-gradient(to right, transparent, ${isDarkMode ? '#4b5563' : '#d1d5db'}, transparent)`,
                            }}
                          />
                        </div>

                        {/* English meaning */}
                        <div className="px-5 pb-4 pt-2 text-center">
                          <p className={`text-sm italic leading-snug ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            {clip.english || '—'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!learnLoading && filteredLearnClips.length > 0 && (
                <p className={`text-center text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {filteredLearnClips.length} audio{filteredLearnClips.length === 1 ? '' : 's'}
                </p>
              )}

                </div>{/* end main content column */}
              </div>{/* end flex split (sidebar + main) */}
            </div>
          )}
        </div>

        {/* Scroll-to-top FAB — mobile only, hugs the left viewport edge, appears after 400px scroll */}
        {isMobile && showTopBtn && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Back to top"
            className="fixed bottom-20 left-1 z-[60] w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg flex items-center justify-center transition-transform active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        )}

        {/* Mobile Category Slide Panel — matches VerbExplorer lines 685-746 */}
        {mobileCatOpen && isMobile && (
          <>
            <div
              className="fixed inset-0 z-[998] bg-black/40"
              onClick={() => setMobileCatOpen(false)}
              style={{ animation: 'fadeIn 0.2s ease-out' }}
              aria-hidden="true"
            />
            <div
              className={`fixed top-0 right-0 bottom-0 z-[999] w-[300px] max-w-[85vw] overflow-y-auto ${
                isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-b from-gray-50 to-white'
              }`}
              style={{ boxShadow: '-4px 0 30px rgba(0,0,0,0.15)', animation: 'slideInRight 0.3s cubic-bezier(0.4,0,0.2,1)' }}
              role="dialog"
              aria-modal="true"
              aria-label="Categories"
            >
              <div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-inherit border-gray-200'}`}>
                <h3 className={`text-base font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Categories</h3>
                <button
                  onClick={() => setMobileCatOpen(false)}
                  aria-label="Close categories"
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-base ${
                    isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >✕</button>
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {learnCategories.map(cat => {
                  const cs = getCatStyle(cat, isDarkMode);
                  const isActive = learnCategory === cat;
                  const label = cat === 'all' ? 'All' : cat;
                  const count = cat === 'all' ? totalLearnCount : (catCounts[cat] || 0);
                  return (
                    <button
                      key={cat}
                      onClick={() => { setLearnCategory(cat); setMobileCatOpen(false); }}
                      className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all"
                      style={{
                        background: isActive ? cs.activeBg : (isDarkMode ? 'transparent' : 'white'),
                        border: `1.5px solid ${isActive ? cs.activeBorder : 'transparent'}`,
                      }}
                    >
                      {renderCatIcon(cat, 28)}
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-bold truncate"
                          style={{ color: isActive ? cs.activeText : (isDarkMode ? '#e2e8f0' : '#1e293b') }}
                        >{label}</div>
                        <div className="text-xs" style={{ color: '#94a3b8' }}>{count} audio{count === 1 ? '' : 's'}</div>
                      </div>
                      <span
                        className="text-base"
                        style={{ color: cs.badge, opacity: isActive ? 1 : 0 }}
                        aria-hidden="true"
                      >{'✓'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading clips...</p>
        </div>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8">
          <span className="text-5xl block mb-3">🎧</span>
          <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No clips available yet</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Admin needs to add audio clips</p>
          <button onClick={() => setActiveView('hub')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-all">
            Back
          </button>
        </div>
      </div>
    );
  }

  const clip = clips[currentClip];

  return (
    <div className={`min-h-screen pb-24 ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setActiveView('hub')}
            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}>
            Back
          </button>
          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {currentClip + 1} / {clips.length}
          </span>
        </div>
        <div className={`h-1.5 rounded-full mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentClip + (answered ? 1 : 0)) / clips.length) * 100}%` }} />
        </div>
        <div className="text-center mb-8">
          <button
            onClick={() => playAudio(clip.audio_url)}
            className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-5xl shadow-xl hover:from-purple-500 hover:to-indigo-500 transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center mx-auto"
          >
            🔊
          </button>
          <p className={`text-sm mt-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {audioPlayed ? 'Tap to play again' : 'Tap to listen'}
          </p>
        </div>
        <div className="space-y-3">
          {clip.options.map((option, idx) => {
            let btnClass = isDarkMode ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50';
            if (answered) {
              if (idx === clip.correct_index) btnClass = 'bg-emerald-500 border-emerald-500 text-white';
              else if (idx === selectedOption) btnClass = 'bg-red-500 border-red-500 text-white';
              else btnClass = isDarkMode ? 'bg-gray-800/50 border-gray-700 text-gray-500' : 'bg-gray-100 border-gray-200 text-gray-400';
            }
            return (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                disabled={answered || !audioPlayed}
                className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all ${btnClass} disabled:cursor-not-allowed`}
              >
                {option}
              </button>
            );
          })}
        </div>
        {!audioPlayed && (
          <p className={`text-center text-sm mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Listen to the audio first before selecting
          </p>
        )}

        {/* Skip button — advance to next clip without registering an answer */}
        <div className="flex justify-center mt-6">
          <button
            onClick={handleSkip}
            disabled={answered}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              answered
                ? 'opacity-30 cursor-not-allowed ' + (isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400')
                : isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border border-gray-600'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm'
            }`}
          >
            <span>Skip</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
};



export default ExploreHub;
